import { Api } from "./api";
import config from "../utils/config";
import firebaseApp, { fireBaseLogEvent } from "../utils/firebase";
import firebase from "firebase";
import { Dispatch } from "redux";
import axios, { AxiosResponse, AxiosError } from "axios";
import { USER_ROLES } from "../interfaces/roles";
import { ILoginResponseAction } from "../redux/actions/user/userAction";
import { stringify } from "querystring";
import {
	loginResponseAction,
	logoutAction,
	getProfileResponseAction,
	selectBrandResponseAction
} from "../redux/actions/user/userActions";
import storage from "../utils/storage";
import { push } from "connected-react-router";
import { routes } from "../routes/routes";
import { HUBSPOT_POST_URL, normalizeHubspotFormData } from "../utils/hubspot";
import { ILoginUserResponse, IRegisterRequest, IProfileRequest } from "../interfaces/user";
import notification, { info } from "../utils/notification";
import { IApplicationState } from "../redux/reducers";
import {
	userPreferenceResponseAction,
	getAllCompanyUsersAction
} from "../redux/actions/user/userActions";
import decode from "jwt-decode";
import notificationService from "./notificationService";
import { IBrand } from "../interfaces/company";
import { error as notificationError } from "../utils/notification";
import { setSearchTerm } from "../redux/actions/ingredients/ingredientActions";

export class UserApi extends Api {
	updateProfile(id: string, userData: IProfileRequest, resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const { user_photo, ...filteredUserData } = userData;
				const response = await this.http.put(
					`${config.api.endpoints.user.profile}${id}/`,
					filteredUserData
				);
				dispatch(getProfileResponseAction(response.data));
				info({
					title: "Profile",
					description: "Your profile has been updated successfully."
				});
			} catch (error) {
				this.handleError(error, "Profile Error");
			}
		};
	}

	getUserProfile(id?: string, resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			const {
				user: { user_id }
			} = getState();
			try {
				const response = await this.http.get(config.api.endpoints.user.profile + `${user_id}/`);
				dispatch(getProfileResponseAction(response.data));
			} catch (error) {
				this.handleError(error, "Profile Error");
			}
		};
	}

	login(username: string, password: string, resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<ILoginResponseAction | any>) => {
			try {
				const response: AxiosResponse<ILoginUserResponse> = await this.http.post(
					config.api.endpoints.login,
					stringify({
						email: username,
						password
					}),
					{
						headers: {
							"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
						}
					}
				);
				if (response.data.action_required) {
					dispatch(loginResponseAction(response.data));
					return;
				}
				this.setSession(response.data);
				dispatch(setSearchTerm(""));
				dispatch(loginResponseAction(response.data));
				const payload = decode(response.data.access_token, {
					header: true
				}) as any;
				notificationService.saveDeviceToken(response.data.access_token);
				this.firebaseSignInWithCustomToken(response.data.access_token);
				fireBaseLogEvent("login", { method: "email_password", email: username });
				// Commented out to prevent 'Check internet connection' error message
				// because payload does not return a claims key|value pair ... should it?
				// dispatch(push(payload.claims.role === USER_ROLES.ADMIN ? routes.ADMIN : routes.DASHBOARD))
				if (resolve) {
					resolve(response);
				}
			} catch (error) {
				this.handleError(error, "Login Error");
			}
		};
	}

	firebaseSignInWithCustomToken(access_token: string) {
		firebase
			.auth()
			.signInWithCustomToken(access_token)
			.catch((error) => {
				const errorCode = error.code;
				const errorMessage = error.message;
				if (errorCode === "auth/invalid-custom-token") {
					this.handleError(error, "Invalid Token");
				} else {
					console.error(error);
				}
			})
			.finally(() => {
				console.info("Successfully logged in with custom token");
			});
	}

	loginWithFirebase(username: string, password: string) {
		return async (dispatch: any) => {
			const auth = firebase.auth();
			const credential = firebase
				.auth()
				.signInWithEmailAndPassword(username, password)
				.then((res) => {
					return res.user;
				})
				.catch((error) => {
					if (error.code == "auth/multi-factor-auth-required") {
						// The user is a multi-factor user. Second factor challenge is required.
						return error;
						// ...
					} else if (error.code == "auth/wrong-password") {
						// Handle other errors such as wrong password.
						return notificationError({ title: "Login Error", description: "Incorrect password" });
					}
				});
			return credential;
		};
	}

	recoveryPasswordWithFirebase(code: string, password: string) {
		return async (dispatch: Dispatch) => {
			try {
				await firebase.auth().confirmPasswordReset(code, password);
				info("Password reset successfully");
			} catch (error) {
				notification.error({
					title: "Reset password",
					description: error.message
				});
				throw new Error(error);
			}
		};
	}

	verifyEmailWithFirebase(code: string) {
		return async (dispatch: Dispatch) => {
			try {
				await firebase.auth().applyActionCode(code);
				info("Email verified successfully");
			} catch (error) {
				notification.error({
					title: "Email verification",
					description: error.message
				});
				throw new Error(error);
			}
		};
	}

	verfiyPassword(password: string, resolve?: Function, reject?: Function) {
		return async (dispatch: Dispatch<ILoginResponseAction | any>) => {
			const firebaseUser = firebaseApp.auth().currentUser;
			if (!firebaseUser || !firebaseUser.email) return;
			var credential = firebase.auth.EmailAuthProvider.credential(firebaseUser.email, password);
			const authResult = await firebaseUser
				?.reauthenticateWithCredential(credential)
				.then((res) => {
					return res;
				})
				.catch((error) => {
					if (error.code == "auth/multi-factor-auth-required") return error;
					if (error.code == "auth/wrong-password") {
						return notificationError({
							title: "Verification Error",
							description: "Incorrect password"
						});
					}
				});
			return authResult;
		};
	}
	register(
		{
			confirmPassword,
			firstName,
			lastName,
			password,
			jobtitle,
			phoneNumber,
			companyType,
			...rest
		}: IRegisterRequest,
		resolve?: Function,
		reject?: Function
	) {
		return async (dispatch: Dispatch<ILoginResponseAction | any>) => {
			try {
				const hubspotData = normalizeHubspotFormData({
					firstname: firstName,
					lastname: lastName,
					jobtitle,
					...rest
				});
				/*const response: AxiosResponse */
				// await this.http.post(
				// 	`/${process.env.REACT_APP_HUBSPORT_PORTAL_ID}/${process.env.REACT_APP_HUBSPOT_GUID}`,
				// 	hubspotData,
				// 	{
				// 		baseURL: HUBSPOT_POST_URL
				// 	}
				// );
				const response: AxiosResponse = await this.http.post(config.api.endpoints.register, {
					first_name: firstName,
					last_name: lastName,
					password,
					job_title: jobtitle,
					phone_number: phoneNumber,
					company_type: companyType,
					role: USER_ROLES.COMPANY_ADMIN,
					...rest
				});
				fireBaseLogEvent("sign_up", { method: "email_password", email: rest.email });
				return response;
			} catch (e) {
				this.handleError(e, "Registration Error");
				throw e;
			}
		};
	}

	logout() {
		return (dispatch: Dispatch) => {
			storage.clear();
			dispatch(setSearchTerm(""));
			dispatch(logoutAction());
			dispatch(push(routes.LOGIN));
		};
	}

	setUserRefreshToken(refreshToken: string) {
		storage.setItem("refresh_token", refreshToken);
	}

	signUserInWithMFAToken(refreshToken: string) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response: AxiosResponse<ILoginUserResponse> = await this.http.post(
					config.api.endpoints.refreshToken,
					stringify({
						refresh_token: refreshToken
					}),
					{
						headers: {
							"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
						}
					}
				);
				if (response.data.action_required) {
					dispatch(loginResponseAction(response.data));
					return;
				}
				this.setSession(response.data);
				dispatch(loginResponseAction(response.data));
				const payload = decode(response.data.access_token, {
					header: true
				}) as any;
				notificationService.saveDeviceToken(response.data.access_token);
			} catch (error) {
				this.handleError(error, "Login Error");
			}
		};
	}
	getPreferences() {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				user: { user_id }
			} = getState();
			try {
				const response = await this.http(config.api.endpoints.user.preferences(user_id || ""));
				dispatch(userPreferenceResponseAction(response.data.preferences));
			} catch (error) {
				if (error.response?.status === 404) {
					return;
				}
				this.handleError(error, "Preference Error");
			}
		};
	}

	updatePreferences(preferences: any) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const {
				user: { user_id }
			} = getState();
			try {
				await this.http.post(config.api.endpoints.user.preferences(user_id || ""), { preferences });
				info("Preferences updated successfully");
			} catch (error) {
				this.handleError(error, "Preferences Error");
			}
		};
	}

	updateProfileImage(file: File) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const formData = new FormData();
			formData.append("user_photo", file);
			const {
				user: { user_id }
			} = getState();
			try {
				const response = await this.http.post(
					config.api.endpoints.user.profileImage(user_id || ""),
					formData
				);

				info("Profile image updated successfully");
			} catch (error) {
				this.handleError(error, "Profile Image");
			}
		};
	}

	updateCompanyLogo(file: File) {
		return async (dispatch: Dispatch, getState: () => IApplicationState) => {
			const formData = new FormData();
			formData.append("logo", file);
			const {
				user: { user_id, profile }
			} = getState();
			try {
				const response = await this.http.post(
					config.api.endpoints.user.companyLogo(user_id || "", profile?.company.id || ""),
					formData
				);
				info("Company logo updated successfully");
			} catch (error) {
				this.handleError(error, "Company Logo");
			}
		};
	}

	sendResetPasswordMail(email: string) {
		return async (dispatch: Dispatch) => {
			try {
				await firebase.auth().sendPasswordResetEmail(email);

				notification.info({
					title: "Reset password",
					description: "Check your email for the reset password link"
				});
			} catch (error) {
				notification.error({
					title: "Reset password",
					description: error.message
				});
				throw new Error(error);
			}
		};
	}

	getAllCompanyUsers() {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.get(config.api.endpoints.user.users);
				dispatch(getAllCompanyUsersAction(response.data));
			} catch (error) {
				this.handleError(error, "Get All Users");
			}
		};
	}

	addCompanyUser(email: string) {
		return async (dispatch: Dispatch) => {
			try {
				const response = await this.http.post(config.api.endpoints.user.addCompanyUser, { email });

				info("User successfully invited.");
			} catch (error) {
				this.handleError(error, "Add Company User");
			}
		};
	}

	updateUserRole(userId: string, role: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.patch(config.api.endpoints.user.updateUserRole(userId), {
					role
				});

				info("User role successfully updated.");
			} catch (error) {
				this.handleError(error, "Update User Role");
			}
		};
	}

	removeUser(userId: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.delete(config.api.endpoints.user.removeUser(userId));
				info("User successfully removed.");
			} catch (error) {
				this.handleError(error, "User Remove");
			}
		};
	}

	getBrands() {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.get(config.api.endpoints.user.brands);
				// Handle the actual response structure: { data: [...], meta: {...} }
				if (response.data && response.data.data && Array.isArray(response.data.data)) {
					
					return response.data.data;
				}

				// Handle the nested response structure: [{ data: { ... } }]
				if (Array.isArray(response.data) && response.data.length > 0) {
					// Extract brands from the nested structure
					const brandsData = response.data.map((item: any) => {
						
						if (item.data) {
							return item.data;
						}
						return item;
					});
					
					return brandsData;
				}

				// Fallback for other response structures
			
				return response.data || [];
			} catch (error) {
				console.error('Error in getBrands:', error);
				this.handleError(error, "Brands error");
				return [];
			}
		};
	}

	getBrandsPaginated(page: number = 1, perPage: number = 30) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				// Validate and sanitize inputs to prevent API errors
				// Ensure page is at least 1 (floors decimal values and handles negative/zero)
				const validPage = Math.max(1, Math.floor(page || 1));
				// Ensure perPage is between 1 and 100 (reasonable limits to prevent performance issues)
				const validPerPage = Math.max(1, Math.min(100, Math.floor(perPage || 30)));

				const response = await this.http.get(
					`${config.api.endpoints.user.brands}?page=${validPage}&per_page=${validPerPage}`
				);

				
				// Handle the paginated response structure: { data: [...], meta: {...} }
				if (response.data && response.data.data && Array.isArray(response.data.data)) {
					return {
						data: response.data.data,
						meta: response.data.meta
					};
				}

				// Handle the nested response structure: [{ data: { ... } }]
				if (Array.isArray(response.data) && response.data.length > 0) {
					// Extract brands from the nested structure
					const brandsData = response.data.map((item: any) => {
						if (item.data) {
							return item.data;
						}
						return item;
					});
					return {
						data: brandsData,
						meta: { current_page: validPage, total_pages: 1, total_items: brandsData.length, per_page: validPerPage }
					};
				}

				// Fallback for other response structures
				return {
					data: response.data || [],
					meta: { current_page: validPage, total_pages: 1, total_items: 0, per_page: validPerPage }
				};
			} catch (error) {
				console.error('Error in getBrandsPaginated:', error);
				this.handleError(error, "Brands pagination error");
				// Use validated values in error response for consistency
				const validPage = Math.max(1, Math.floor(page || 1));
				const validPerPage = Math.max(1, Math.min(100, Math.floor(perPage || 30)));
				return {
					data: [],
					meta: { current_page: validPage, total_pages: 0, total_items: 0, per_page: validPerPage }
				};
			}
		};
	}

	getManufacturers() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.user.manufacturers);
				return response.data;
			} catch (error) {
				this.handleError(error, "Manufacturers error");
				return [];
			}
		};
	}

	// getCountries() {
	// 	return async (dispatch: Dispatch<any>) => {
	// 		try {
	// 			const response = await this.http.get(config.api.endpoints.countries.list);
	// 			return response.data;
	// 		} catch (error) {
	// 			this.handleError(error, "Countries error");
	// 			return [];
	// 		}
	// 	};
	// }
	async getCountries() {
		try {
			const response = await this.http.get(config.api.endpoints.countries.list);
			return response.data;
		} catch (error) {
			this.handleError(error, "Countries error");
			return [];
		}
	}

	getCurrencies() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.currencies.list);
				return response.data;
			} catch (error) {
				this.handleError(error, "Currencies error");
				return [];
			}
		};
	}

	addBrand(name: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.post(config.api.endpoints.user.brands, { name });
				return response.data;
			} catch (error) {
				this.handleError(error, "Brands error");
			}
		};
	}

	removeBrand(id: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.delete(config.api.endpoints.user.brands + id + "/");
				return response.data;
			} catch (error) {
				this.handleError(error, "Remove Brand error");
			}
		};
	}

	editBrand(id: string, name: string) {
		return async (dispatch: Dispatch<any>, getState: () => IApplicationState) => {
			try {
				const response = await this.http.put(config.api.endpoints.user.brands + id + "/", { name });
				return response.data;
			} catch (error) {
				this.handleError(error, "Remove Brand error");
			}
		};
	}

	setWalkthroughComplete(id: string, complete = true) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.patch(config.api.endpoints.user.walkthrough(id), {
					walkthrough: !complete
				});
			} catch (error) {
				this.handleError(error, "Marking Walkthrough Complete");
			}
		};
	}

	setSelectedBrand(brand?: IBrand) {
		return async (dispatch: Dispatch<any>) => {
			try {
				dispatch(selectBrandResponseAction(brand));
			} catch (error) {
				this.handleError(error, "Setting Selected Brand");
			}
		};
	}

	sendRequestToAdmin() {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.post(config.api.endpoints.user.accessRequest, {});
				return response.data;
			} catch (error) {
				this.handleError(error, "Request to Admin");
				// notification.error({
				// 	title: "Reset password",
				// 	description: error.message
				// });
				return [];
			}
		};
	}

	approveRequestOfUser(userId: string) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.post(config.api.endpoints.user.approveRequest(userId));
				return response.data;
			} catch (error) {
				this.handleError(error, "Approve request");
				// notification.error({
				// 	title: "Reset password",
				// 	description: error.message
				// });
				return [];
			}
		};
	}

	getUserById(userId: string) {
		return async (dispatch: Dispatch<any>) => {
			try {
				const response = await this.http.get(config.api.endpoints.admin.editUser(userId));
				return response.data;
			} catch (error) {
				this.handleError(error, "Fetch User Error");
				throw error;
			}
		};
	}

}

export default new UserApi();